import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { curSym as sym } from "@/lib/currency";

const fmt = (v, c) => (v == null ? "—" : `${Number(v).toLocaleString("fr-FR")} ${sym(c)}`);
const fmtMulti = (obj) => Object.entries(obj || {}).map(([c, v]) => fmt(v, c)).join(" · ") || "—";

// Tableau de répartition (revenus par …) — chiffres utiles, pas de graphiques inutiles
const Breakdown = ({ title, data, testid }) => (
  <div className="border border-border bg-card p-3" data-testid={testid}>
    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</div>
    <div className="mt-2 space-y-1 text-sm">
      {Object.entries(data || {}).sort((a, b) => Object.values(b[1]).reduce((x, y) => x + y, 0) - Object.values(a[1]).reduce((x, y) => x + y, 0)).map(([k, v]) => (
        <div key={k} className="flex justify-between border-b border-border/50 last:border-0 pb-0.5">
          <span className="truncate mr-2">{k}</span><span className="font-semibold whitespace-nowrap">{fmtMulti(v)}</span>
        </div>
      ))}
      {Object.keys(data || {}).length === 0 && <p className="text-xs text-muted-foreground">Aucune donnée.</p>}
    </div>
  </div>
);

export const AdminAnalytics = ({ hub = "all", src = "all" }) => {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("all");
  const [dest, setDest] = useState("all");
  const [period, setPeriod] = useState("all");
  const [range, setRange] = useState({ from: "", to: "" });
  useEffect(() => {
    api.get(`/admin/analytics?hub=${hub}&src=${src}&mode=${mode}&dest=${encodeURIComponent(dest)}&period=${period}&date_from=${range.from}&date_to=${range.to}`)
      .then(({ data }) => setData(data)).catch(() => {});
  }, [hub, src, mode, dest, period, range]);
  if (!data) return <p className="text-sm text-muted-foreground">Chargement…</p>;
  const curs = Object.keys(data.kpis);

  return (
    <div data-testid="analytics-panel">
      {/* Filtres : mode / destination / période (hub + source via la barre du haut) */}
      <div className="flex flex-wrap items-center gap-2" data-testid="analytics-filters">
        <select data-testid="ana-mode" className="h-8 border border-border bg-card px-2 text-sm" value={mode} onChange={(e) => setMode(e.target.value)}>
          <option value="all">Air + Maritime</option><option value="air">Aérien / Express</option><option value="sea">Maritime</option>
        </select>
        <select data-testid="ana-dest" className="h-8 border border-border bg-card px-2 text-sm" value={dest} onChange={(e) => setDest(e.target.value)}>
          <option value="all">Toutes destinations</option>
          {(data.destinations || []).map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <div className="flex border border-border">
          {[["all", "Tout"], ["today", "Aujourd’hui"], ["week", "7 j"], ["month", "30 j"], ["custom", "Personnalisée"]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`ana-period-${v}`} onClick={() => setPeriod(v)}
              className={`px-3 py-1.5 text-xs font-semibold ${period === v ? "bg-primary text-white" : "bg-card hover:bg-secondary"}`}>{l}</button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex items-center gap-1.5">
            <Input type="date" className="rounded-sm h-8 w-36" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
            <span className="text-xs">→</span>
            <Input type="date" className="rounded-sm h-8 w-36" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
          </div>
        )}
      </div>

      {curs.length === 0 && <p className="mt-4 text-sm text-muted-foreground">Aucune donnée sur cette sélection.</p>}
      {curs.map((c) => {
        const k = data.kpis[c];
        const cells = [
          ["Clients actifs", k.clients_actifs], ["Colis reçus", k.colis_recus],
          ["Kg reçus", `${k.kg_recus} kg`], ["Kg expédiés", `${k.kg_expedies} kg`],
          ["Chiffre d’affaires", fmt(k.ca, c)], ["Coût moyen / kg", fmt(k.cout_moyen_kg, c)],
          ["Prix moyen / kg", fmt(k.prix_moyen_kg, c)], ["Marge moyenne / kg", fmt(k.marge_moyenne_kg, c)],
          ["Panier moyen", fmt(k.panier_moyen, c)], ["Factures générées", k.factures],
          ["Factures payées", k.payees], ["Paiements en attente", k.attente],
        ];
        return (
          <div key={c} className="mt-4">
            {curs.length > 1 && <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">Devise {c} ({sym(c)})</div>}
            <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-6">
              {cells.map(([label, val], i) => (
                <div key={i} className="border border-border bg-card p-3" data-testid={`ana-kpi-${i}-${c}`}>
                  <div className="font-heading font-extrabold text-xl">{val ?? "—"}</div>
                  <div className="text-[11px] text-muted-foreground leading-tight">{label}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <div className="mt-5 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <Breakdown title="Air vs Maritime" data={data.by_mode} testid="ana-by-mode" />
        <Breakdown title="Revenus par destination" data={data.by_dest} testid="ana-by-dest" />
        <Breakdown title="Revenus par Hub" data={data.by_hub} testid="ana-by-hub" />
        <Breakdown title="Revenus par type d’activité" data={data.by_activity} testid="ana-by-activity" />
      </div>
    </div>
  );
};
